import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  fetchEmailProvider,
  getEmailStatus,
  updateEmailProvider,
  type EmailProviderState,
} from "../../api/emailLogsApi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const PROVIDER_LABELS: Record<string, string> = {
  mailjet: "Mailjet",
  resend: "Resend",
  smtp: "SMTP",
  ses: "Amazon SES",
};

const providerLabel = (value?: string | null) => {
  if (!value) {
    return "Unknown";
  }
  return PROVIDER_LABELS[value] ?? value;
};

const EmailProviderPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState<EmailProviderState | null>(null);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [envFrom, setEnvFrom] = useState<string | undefined>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [providerState, status] = await Promise.all([
        fetchEmailProvider(),
        getEmailStatus(),
      ]);
      setState(providerState);
      setSelectedProvider(providerState.provider);
      setEnvFrom(status.serviceInfo?.from);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to load email provider settings.";
      toast.error(message);
      setState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const availableProviders =
    state?.available && state.available.length > 0
      ? state.available
      : state?.provider
        ? [state.provider]
        : [];

  const handleSave = async () => {
    if (!selectedProvider) {
      toast.error("Select a provider.");
      return;
    }
    setSaving(true);
    try {
      const updated = await updateEmailProvider(selectedProvider);
      setState(updated);
      setSelectedProvider(updated.provider);
      toast.success("Email provider updated.");
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to update email provider.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const dirty = Boolean(state && selectedProvider && selectedProvider !== state.provider);

  return (
    <div className="space-y-6 p-4 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>Provider settings</CardTitle>
          <p className="text-sm text-gray-600">
            Runtime provider override for outbound mail. Falls back to{" "}
            <code className="text-xs">EMAIL_SERVICE</code> in backend env when unset.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <p className="text-sm text-gray-500">Loading provider settings…</p>
          ) : !state ? (
            <p className="text-sm text-gray-500">
              Provider settings are unavailable. Deploy the email provider API or check admin
              permissions.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-gray-600">Active:</span>
                <Badge variant="secondary">{providerLabel(state.provider)}</Badge>
                {state.source ? (
                  <Badge variant="outline">{state.source === "runtime" ? "Runtime override" : "Env default"}</Badge>
                ) : null}
              </div>
              {envFrom ? (
                <p className="text-sm text-gray-600">
                  From address: <span className="font-mono">{envFrom}</span>
                </p>
              ) : null}
              <div className="space-y-2">
                <Label htmlFor="email-provider-select">Provider</Label>
                <Select
                  value={selectedProvider}
                  onValueChange={setSelectedProvider}
                  disabled={saving || availableProviders.length === 0}
                >
                  <SelectTrigger id="email-provider-select">
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableProviders.map((provider) => (
                      <SelectItem key={provider} value={provider}>
                        {providerLabel(provider)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={handleSave} disabled={!dirty || saving}>
                  {saving ? "Saving…" : "Save provider"}
                </Button>
                <Button type="button" variant="outline" onClick={() => void load()} disabled={loading || saving}>
                  Refresh
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailProviderPanel;
